import { createAction } from "@reduxjs/toolkit";
import _ from "lodash";
import axios from "axios";
import { takeEvery, call, put, select } from "redux-saga/effects";
import buildUrl from "build-url-ts";
import { 
  FETCH_CALL_RECORDINGS_REQUEST,
  FETCHING_CALL_RECORDINGS_SUCCEEDED,
  FETCHING_CALL_RECORDINGS_FAILED,
  GENERATE_TRANSCRIPTS_FOR_CALL_RECORDING_REQUEST,
  GENERATING_TRANSCRIPTS_FOR_CALL_RECORDING_SUCCEEDED,
  GENERATING_TRANSCRIPTS_FOR_CALL_RECORDING_FAILED,
  DELETE_CALL_RECORDING_REQUEST,
  DELETING_CALL_RECORDING_SUCCEEDED,
  DELETING_CALL_RECORDING_FAILED,
  FETCH_CALL_RECORDING_REQUEST,
  FETCHING_CALL_RECORDING_SUCCEEDED,
  FETCHING_CALL_RECORDING_FAILED,
  SYNC_CALL_RECORDING_PROFILE_PROCESSING_STATE_REQUEST,
  SYNCING_CALL_RECORDING_PROFILE_PROCESSING_STATE_SUCCEEDED,
  SYNCING_CALL_RECORDING_PROFILE_PROCESSING_STATE_FAILED
} from "../actionTypes";

import { 
  FetchCallRecordingsRequest,
  FetchCallRecordingsResponse,
  FetchCallRecordingsErrorResponse,
  DeleteCallRecordingRequest,
  DeleteCallRecordingResponse,
  DeleteCallRecordingErrorResponse,
  GenerateTranscriptForCallRecordingRequest,
  GenerateTranscriptForCallRecordingResponse,
  GenerateTranscriptForCallRecordingErrorResponse,
  FetchCallRecordingRequest,
  FetchCallRecordingResponse,
  FetchCallRecordingErrorResponse
} from "src/types/coaching/callRecording";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_COACHING_API_URL}`;

// fetch call recording
export const fetchCallRecording = createAction<FetchCallRecordingRequest>(FETCH_CALL_RECORDING_REQUEST);
const fetchingCallRecordingSucceeded = createAction<FetchCallRecordingResponse>(FETCHING_CALL_RECORDING_SUCCEEDED);
const fetchingCallRecordingFailed = createAction<FetchCallRecordingErrorResponse>(FETCHING_CALL_RECORDING_FAILED);

const fetchCallRecordingSaga = function* (action: ReturnType<typeof fetchCallRecording>): any {
  const { id } = action.payload as FetchCallRecordingRequest;

  const url = `${apiUrl}/api/1.0/call_recordings/${id}`;

  const fetchCallRecordingApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCallRecordingApi);
    yield put(fetchingCallRecordingSucceeded({id: id, response}));
  } catch (error) {
    yield put(fetchingCallRecordingFailed({id: id, error }));
  }
};


// fetch call recordings
export const fetchCallRecordings = createAction<FetchCallRecordingsRequest>(FETCH_CALL_RECORDINGS_REQUEST);
const fetchCallRecordingsSucceeded = createAction<FetchCallRecordingsResponse>(FETCHING_CALL_RECORDINGS_SUCCEEDED);
const fetchCallRecordingsFailed = createAction<FetchCallRecordingsErrorResponse>(FETCHING_CALL_RECORDINGS_FAILED);

const fetchCallRecordingsSaga = function* (action: ReturnType<typeof fetchCallRecordings>): any {
  const { page, limit  } = action.payload as FetchCallRecordingsRequest;

  const url = `${apiUrl}/api/1.0/call_recordings`;

  const fetchCallRecordingsApi = () => {
    return axios({
      method: "GET",
      url: url,
      params: {
        page: page,
        limit: limit,
      },
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCallRecordingsApi);
    yield put(fetchCallRecordingsSucceeded({response: response}));
  } catch (error) {
    yield put(fetchCallRecordingsFailed({error}));
  }
};

// fetch transcript for call recording
export const generateTranscriptsForCallRecording = createAction<GenerateTranscriptForCallRecordingRequest>(GENERATE_TRANSCRIPTS_FOR_CALL_RECORDING_REQUEST);
const generateTranscriptsForCallRecordingSucceeded = createAction<GenerateTranscriptForCallRecordingResponse>(GENERATING_TRANSCRIPTS_FOR_CALL_RECORDING_SUCCEEDED);
const generateTranscriptsForCallRecordingFailed = createAction<GenerateTranscriptForCallRecordingErrorResponse>(GENERATING_TRANSCRIPTS_FOR_CALL_RECORDING_FAILED);

const generateTranscriptsForCallRecordingSaga = function* (action: ReturnType<typeof generateTranscriptsForCallRecording>): any {
  const { id, signedUrl } = action.payload;

  const generateTranscriptionAPI = () => {
    return axios({
      method: "POST",
      url: `${apiUrl}/api/1.0/call_recordings/upload`,
      data: {
        signedUrl: signedUrl,   
        id: id
      }
    }).then((response) => response.data);
  };

  try {
    const response = yield call(generateTranscriptionAPI);
    yield put(
      generateTranscriptsForCallRecordingSucceeded({
        id: response.id,
        response: response,
      }),
    );
  } catch (error) {
    yield put(generateTranscriptsForCallRecordingFailed({ error: error }));
  }
};

export const deleteCallRecording = createAction<DeleteCallRecordingRequest>(DELETE_CALL_RECORDING_REQUEST);
const deletingCallRecordingSucceeded = createAction<DeleteCallRecordingResponse>(DELETING_CALL_RECORDING_SUCCEEDED);
const deletingCallRecordingFailed = createAction<DeleteCallRecordingErrorResponse>(DELETING_CALL_RECORDING_FAILED);

const deleteCallRecordingSaga = function* (action: ReturnType<typeof deleteCallRecording>): any {
  const { id } = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/call_recordings/${id}`,
  });

  const deleteCallRecordingApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(deleteCallRecordingApi);
    yield put(deletingCallRecordingSucceeded(response));
  } catch (error) {
    yield put(deletingCallRecordingFailed({ id: id, error: error }));
  }
};


//Sync call recording processing state
export const syncCallRecordingProcessingState = createAction<string>(SYNC_CALL_RECORDING_PROFILE_PROCESSING_STATE_REQUEST);
const syncingCallRecordingProcessingStateSucceeded = createAction<FetchCallRecordingResponse>(SYNCING_CALL_RECORDING_PROFILE_PROCESSING_STATE_SUCCEEDED);
const syncingCallRecordingProcessingStateFailed = createAction<FetchCallRecordingErrorResponse>(SYNCING_CALL_RECORDING_PROFILE_PROCESSING_STATE_FAILED);

const syncCallRecordingProcessingStateSaga = function* (
  action: ReturnType<typeof syncCallRecordingProcessingState>,
): any {
  const id = action.payload;

  const url = `${apiUrl}/api/1.0/call_recordings/${id}`;

  const fetchCallRecordingApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchCallRecordingApi);
    yield put(syncingCallRecordingProcessingStateSucceeded({id: id, response}));
  } catch (error) {
    yield put(syncingCallRecordingProcessingStateFailed({id: id, error }));
  }
};


export default function* callRecordingSaga() {
  yield takeEvery(FETCH_CALL_RECORDING_REQUEST, fetchCallRecordingSaga);
  yield takeEvery(FETCH_CALL_RECORDINGS_REQUEST, fetchCallRecordingsSaga);
  yield takeEvery(GENERATE_TRANSCRIPTS_FOR_CALL_RECORDING_REQUEST, generateTranscriptsForCallRecordingSaga);
  yield takeEvery(DELETE_CALL_RECORDING_REQUEST, deleteCallRecordingSaga);
  yield takeEvery(SYNC_CALL_RECORDING_PROFILE_PROCESSING_STATE_REQUEST, syncCallRecordingProcessingStateSaga);
}